import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PackageMinus } from "lucide-react";
import { inventoryApi, inventoryQueryKeys } from "../../../shared/api/inventory";
import {
  clinicFeaturesApi,
  clinicFeaturesQueryKeys,
} from "../../../shared/api/clinicFeatures";
import { apiError } from "../../../shared/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

/**
 * Cấp thuốc từ đơn → trừ tồn kho (FEFO). Only shown when the clinic enabled
 * "Trừ tồn kho khi cấp thuốc" (ADR-0027 Phase 3b). Idempotent: once dispensed,
 * shows the batches deducted instead of the button.
 */
export function DispensePanel({ prescriptionId }: { prescriptionId: string }) {
  const qc = useQueryClient();
  const features = useQuery({
    queryKey: clinicFeaturesQueryKeys.current(),
    queryFn: () => clinicFeaturesApi.get(),
    staleTime: 5 * 60 * 1000,
  });
  const enabled = features.data?.deductStockOnDispense === true;
  const key = inventoryQueryKeys.dispenseEvents(prescriptionId);
  const events = useQuery({
    queryKey: key,
    queryFn: () => inventoryApi.dispenseEvents(prescriptionId),
    enabled,
  });
  const [err, setErr] = useState<string | null>(null);
  const dispense = useMutation({
    mutationFn: () => inventoryApi.dispense(prescriptionId),
    onSuccess: () => {
      setErr(null);
      qc.invalidateQueries({ queryKey: key });
      qc.invalidateQueries({ queryKey: inventoryQueryKeys.stock() });
    },
    onError: (e) => setErr(apiError(e).detail),
  });

  if (!enabled) return null;
  const done = (events.data?.length ?? 0) > 0;

  return (
    <Card className="mt-4">
      <CardContent className="p-5">
        <h3 className="mb-1 text-base font-semibold text-foreground">Cấp thuốc &amp; tồn kho</h3>
        {done ? (
          <>
            <p className="mb-2 text-sm text-emerald-600">Đã cấp phát — đã trừ tồn kho.</p>
            <ul className="space-y-1 text-sm">
              {events.data!.map((ev) => (
                <li key={ev.id} className="flex items-center justify-between gap-3">
                  <span className="min-w-0 truncate">{ev.medicineName}</span>
                  <span className="shrink-0 tabular-nums text-muted-foreground">
                    {ev.quantity} · lô {ev.batchId.slice(0, 8)}
                  </span>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <>
            <p className="mb-3 text-sm text-muted-foreground">
              Cấp thuốc theo đơn và trừ tồn kho (FEFO — lô hết hạn sớm cấp trước).
            </p>
            <Button type="button" disabled={dispense.isPending} onClick={() => dispense.mutate()}>
              <PackageMinus />
              {dispense.isPending ? "Đang cấp…" : "Cấp thuốc & trừ tồn kho"}
            </Button>
          </>
        )}
        {err && <p className="mt-2 text-sm text-destructive">{err}</p>}
      </CardContent>
    </Card>
  );
}
